// components/UserAgreements.js
import React, { useState, useMemo } from "react";
import {
  Spinner,
  Dropdown,
  Button,
  Container,
  Row,
  Col,
  Modal,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import paths from "routes/paths";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import SubtleBadge from "components/common/SubtleBadge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import IconButton from "components/common/IconButton";
import CustomPaginationFooter from "components/common/CustomPaginationFooter";
import { toast } from "react-toastify";
import useFormattedDate from "hooks/useFormatDate";

import useUserAgreements from "hooks/useUserAgreements";

const UserAgreements = () => {
  const navigate = useNavigate();
  const { agreements, loading, refetch, deleteAgreement } = useUserAgreements();

  // Modal state for delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [agreementToDelete, setAgreementToDelete] = useState(null);
  const { formatDateTime} =useFormattedDate();

  const handleViewAgreement = (id) => {
    navigate(paths.agreementDetails(id));
  };

  const handleEditAgreement = (id) => {
    navigate(paths.agreementEdit(id));
  };

  const handleCreateAgreement = () => {
    navigate(paths.agreementAdd);
  };

  const confirmDelete = (id) => {
    setAgreementToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!agreementToDelete) return;
    try {
      await deleteAgreement(agreementToDelete);
      toast.success("User Agreement deleted successfully");
      await refetch();
    } catch (err) {
      console.error("Failed to delete User Agreement:", err);
      toast.error("Failed to delete User Agreement");
    } finally {
      setShowDeleteModal(false);
      setAgreementToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setAgreementToDelete(null);
  };

  // Status colors for badges
  const statusColors = {
    true: "success",
    false: "secondary",
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "version",
        header: "Version",
        meta: {
          headerProps: { className: "text-900" },
        },
      },
      {
        accessorKey: "id",
        header: "Agreement ID",
        cell: ({ getValue }) => `${getValue().substring(0, 8)}...`,
        meta: {
          headerProps: { className: "text-900" },
        },
      },
      {
        accessorKey: "createdAt",
        header: "Creation Date",
        cell: ({ getValue }) => formatDateTime(getValue()),
        meta: {
          headerProps: { className: "text-900" },
        },
      },
      {
        id: "actions",
        header: "Actions",
        meta: {
          headerProps: { className: "text-900" },
        },
        cell: ({ row }) => {
          const item = row.original;
          return (
            <Dropdown className="action-dropdown p-0">
              <Dropdown.Toggle
                as="button"
                className="bg-transparent border-0 p-0 shadow-none text-secondary"
              >
                ⋯
              </Dropdown.Toggle>
              <Dropdown.Menu className="action-menu">
                <Dropdown.Item onClick={() => handleViewAgreement(item.id)}>
                  View
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleEditAgreement(item.id)}>
                  Edit
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item
                  className="text-danger"
                  onClick={() => confirmDelete(item.id)}
                >
                  Delete
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          );
        },
      },
    ],
    [navigate]
  );

  const table = useAdvanceTable({
    data: agreements,
    columns,
    sortable: true,
    perPage: 10,
    pagination: true,
  });

  if (loading && agreements.length === 0) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="primary" />
        <span
          className="ms-3 text-sm text-primary"
          style={{ fontSize: 13.33 }}
        >
          Loading User Agreements...
        </span>
      </Container>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        className="mt-4 mb-4 px-2 px-md-0"
        title="User Agreements"
        icon="handshake"
        subtitle="Manage all versions of Terms of Service, Privacy Policies, and other legal agreements."
      />

      <AdvanceTableProvider {...table}>
        <Row className="mb-3 g-3 px-2 px-md-0">
          <Col xs={12} md={6} lg={5}>
            <AdvanceTableSearchBox placeholder="Search agreements..." />
          </Col>
          <Col xs={12} md={6} lg={7} className="text-end">
            <IconButton
              variant="falcon-default"
              size="sm"
              icon="plus"
              transform="shrink-3"
              onClick={handleCreateAgreement}
              className="w-100 w-md-auto"
            >
              <span className="d-none d-sm-inline-block ms-1">Add Agreement</span>
              <span className="d-sm-none">Add</span>
            </IconButton>
          </Col>
        </Row>

        <div className="px-2 px-md-0">
          <AdvanceTable
            headerClassName="bg-300 text-nowrap align-middle text-black"
            rowClassName="align-middle white-space-nowrap"
            tableProps={{
              bordered: true,
              striped: true,
              className: "fs-10 mb-0 overflow-hidden table-responsive",
              style: { minWidth: '600px' }
            }}
          />
          <CustomPaginationFooter
            table={table}
            totalEntries={agreements.length}
            loading={loading}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>
      </AdvanceTableProvider>

      <Modal show={showDeleteModal} onHide={handleCancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete Agreement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this <strong>User Agreement</strong>? This
          action is generally not recommended for active agreements.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserAgreements;
